
import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, Book, Check, Clock, Bookmark, 
  ThumbsUp, ThumbsDown, MessageCircle, 
  Info, ExternalLink, Users, Building, Briefcase, 
  Layers, Brain, Bot, Wallet, GitMerge, 
  UserCircle, KeyRound, Mail, ShieldCheck, Image,
  Smartphone, Globe, Monitor, LayoutDashboard, Search,
  Bell, Settings, ChevronRight, Menu, User, Gauge, 
  FileText, BarChart, Calendar, MessageSquare, CircleDollarSign,
  List, HelpCircle, BookOpen
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface GettingStartedContentProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const GettingStartedContent: React.FC<GettingStartedContentProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark,
}) => {
  if (activePage === "platform-overview") {
    return (
      <div className="space-y-6" id="platform-overview">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Platform Overview</h1>
          <p className="text-gray-500 mt-2">
            A comprehensive guide to understanding the DGK Platform and its ecosystem.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: June 15, 2023</span>
            <Badge variant="outline" className="ml-2">Official</Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
          <Card className="bg-gray-50 border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-start">
                <Book className="h-8 w-8 text-blue-500 mr-3" />
                <div>
                  <h3 className="font-medium mb-1">For Beginners</h3>
                  <p className="text-sm text-gray-500">New to the platform? Start here to get familiar with the basics.</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-50 border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-start">
                <Users className="h-8 w-8 text-purple-500 mr-3" />
                <div>
                  <h3 className="font-medium mb-1">For Teams</h3>
                  <p className="text-sm text-gray-500">Learn how to collaborate effectively with team features.</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-50 border-l-4 border-l-emerald-500">
            <CardContent className="pt-6">
              <div className="flex items-start">
                <Building className="h-8 w-8 text-emerald-500 mr-3" />
                <div>
                  <h3 className="font-medium mb-1">For Enterprise</h3>
                  <p className="text-sm text-gray-500">Discover enterprise-grade features and customizations.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="prose max-w-none">
          <h2 id="what-is-dgk-platform">What is DGK Platform?</h2>
          <p>
            DGK Platform is a comprehensive ecosystem designed to connect freelancers, agencies, and clients in a seamless marketplace environment. 
            The platform provides tools for digital portfolio creation, project collaboration, skills testing, AI-driven job matching, and much more.
          </p>
          
          <h2 id="key-features">Key Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-4">
            <div className="flex">
              <div className="mr-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-600">
                  <Briefcase className="h-4 w-4" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-medium mb-1">Project Management</h3>
                <p className="text-sm text-gray-600">Create, manage, and collaborate on projects with advanced tools.</p>
              </div>
            </div>
            
            <div className="flex">
              <div className="mr-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600">
                  <Layers className="h-4 w-4" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-medium mb-1">Digital Portfolio</h3>
                <p className="text-sm text-gray-600">Showcase your work with customizable digital portfolios.</p>
              </div>
            </div>
            
            <div className="flex">
              <div className="mr-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-600">
                  <Brain className="h-4 w-4" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-medium mb-1">AI Job Matching</h3>
                <p className="text-sm text-gray-600">Find the perfect job or talent with AI-powered matching algorithms.</p>
              </div>
            </div>
            
            <div className="flex">
              <div className="mr-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-600">
                  <Bot className="h-4 w-4" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-medium mb-1">AI Agents</h3>
                <p className="text-sm text-gray-600">Leverage AI assistants to boost productivity and automate tasks.</p>
              </div>
            </div>
            
            <div className="flex">
              <div className="mr-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-600">
                  <Wallet className="h-4 w-4" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-medium mb-1">Marketplace</h3>
                <p className="text-sm text-gray-600">Buy and sell services with secure payment and escrow features.</p>
              </div>
            </div>
            
            <div className="flex">
              <div className="mr-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600">
                  <GitMerge className="h-4 w-4" />
                </div>
              </div>
              <div>
                <h3 className="text-base font-medium mb-1">Team Builder</h3>
                <p className="text-sm text-gray-600">Assemble the perfect team for any project with powerful team-building tools.</p>
              </div>
            </div>
          </div>
          
          <h2 id="platform-architecture">Platform Architecture</h2>
          <p>
            The DGK Platform is built on a modern, scalable architecture designed for performance, security, and flexibility.
            The platform consists of several integrated modules:
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
            <h3 className="text-base font-medium mb-2">Core Modules</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 list-none pl-0">
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> User Management</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Project System</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Digital Portfolio</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Marketplace</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> AI Services</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Communication Tools</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Payment Processing</li>
              <li className="flex items-center"><Check className="h-4 w-4 text-emerald-500 mr-2" /> Analytics Engine</li>
            </ul>
          </div>
          
          <Tabs defaultValue="freelancers" className="w-full my-6">
            <TabsList className="w-full">
              <TabsTrigger value="freelancers" className="flex-1">For Freelancers</TabsTrigger>
              <TabsTrigger value="clients" className="flex-1">For Clients</TabsTrigger>
              <TabsTrigger value="agencies" className="flex-1">For Agencies</TabsTrigger>
            </TabsList>
            <TabsContent value="freelancers" className="p-4 bg-gray-50 rounded-md mt-2">
              <h3 className="font-medium mb-2">Key Features for Freelancers</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Digital portfolio to showcase your work</li>
                <li>AI job matching to find relevant opportunities</li>
                <li>Skills tests to verify and promote your expertise</li>
                <li>Project collaboration tools</li>
                <li>Secure payment system with escrow protection</li>
              </ul>
            </TabsContent>
            <TabsContent value="clients" className="p-4 bg-gray-50 rounded-md mt-2">
              <h3 className="font-medium mb-2">Key Features for Clients</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Find qualified freelancers and agencies</li>
                <li>Post projects with detailed requirements</li>
                <li>Review portfolios and past work</li>
                <li>Secure payment protection</li>
                <li>Project tracking and management tools</li>
              </ul>
            </TabsContent>
            <TabsContent value="agencies" className="p-4 bg-gray-50 rounded-md mt-2">
              <h3 className="font-medium mb-2">Key Features for Agencies</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Team builder to assemble perfect project teams</li>
                <li>Agency profile and portfolio management</li>
                <li>Client relationship management</li>
                <li>Project workflow optimization</li>
                <li>Advanced analytics and reporting</li>
              </ul>
            </TabsContent>
          </Tabs>
          
          <h2 id="getting-started">Getting Started</h2>
          <p>
            Ready to dive in? Follow these steps to get started with the DGK Platform:
          </p>
          
          <ol className="space-y-4 pl-5 list-decimal">
            <li>
              <strong>Create an account</strong> - Sign up and complete your profile with relevant skills and experience.
            </li>
            <li>
              <strong>Set up your portfolio</strong> - Showcase your best work using the Digital Portfolio tools.
            </li>
            <li>
              <strong>Explore the marketplace</strong> - Browse projects or services that match your interests.
            </li>
            <li>
              <strong>Take skills tests</strong> - Verify your expertise with our comprehensive skills assessment.
            </li>
            <li>
              <strong>Connect with others</strong> - Build your network by connecting with clients, freelancers, and agencies.
            </li>
          </ol>
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 my-6">
            <h3 className="text-base font-medium text-blue-800 mb-2 flex items-center">
              <Info className="h-5 w-5 mr-2" />
              Pro Tip
            </h3>
            <p className="text-blue-700 text-sm">
              Complete your profile 100% to increase your visibility in search results and AI job matching. 
              Users with complete profiles receive up to 5x more project opportunities!
            </p>
          </div>
        </div>
        
        <div className="border-t border-gray-200 pt-6 mt-8">
          <h3 className="font-medium mb-4">Related Documentation</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="hover:bg-gray-50">
              <CardContent className="p-4">
                <Link to="/documentation?section=getting-started&page=quick-start-guide" className="flex items-center text-blue-600">
                  <Book className="h-4 w-4 mr-2" />
                  Quick Start Guide
                  <ArrowRight className="h-4 w-4 ml-auto" />
                </Link>
              </CardContent>
            </Card>
            <Card className="hover:bg-gray-50">
              <CardContent className="p-4">
                <Link to="/documentation?section=user-guide&page=profile-management" className="flex items-center text-blue-600">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Profile Management
                  <ArrowRight className="h-4 w-4 ml-auto" />
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["platform-overview"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("platform-overview", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("platform-overview", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("platform-overview") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("platform-overview")}
            >
              {bookmarkedArticles.includes("platform-overview") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (activePage === "quick-start-guide") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Quick Start Guide</h1>
          <p className="text-gray-500 mt-2">
            Get started with the DGK Platform in just a few minutes with this step-by-step guide.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: June 20, 2023</span>
            <Badge variant="outline" className="ml-2">Beginner</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="five-minute-setup">Five-Minute Setup</h2>
          <p>
            Follow these simple steps to get your account set up and start using the platform right away:
          </p>
          
          <div className="my-6 space-y-6">
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                  1
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Create Your Account</h3>
                <p className="text-gray-600 mb-3">
                  Sign up with your email or use social login options for faster access.
                </p>
                <div className="bg-gray-50 p-3 rounded-md text-sm">
                  <p className="text-gray-700 mb-2">
                    <strong>Pro Tip:</strong> Using a professional email address helps establish credibility.
                  </p>
                  <Link to="/auth" className="text-blue-600 flex items-center hover:underline">
                    Go to Sign Up <ArrowRight className="h-4 w-4 ml-1" />
                  </Link>
                </div>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                  2
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Complete Your Profile</h3>
                <p className="text-gray-600 mb-3">
                  Add your skills, experience, and portfolio items to increase visibility.
                </p>
                <Card className="border border-gray-200">
                  <CardContent className="p-4">
                    <h4 className="font-medium text-base mb-2">Essential Profile Elements:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Professional profile photo</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Clear bio/description (250+ characters)</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Skills with proficiency levels</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Work experience</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Portfolio items (at least 3 recommended)</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                  3
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Explore the Platform</h3>
                <p className="text-gray-600 mb-3">
                  Familiarize yourself with key features like the marketplace, project area, and messaging.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Link to="/marketplace" className="block text-blue-600 p-3 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors">
                    <div className="font-medium mb-1">Marketplace</div>
                    <div className="text-sm text-blue-700">Browse services and projects</div>
                  </Link>
                  <Link to="/projects" className="block text-blue-600 p-3 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors">
                    <div className="font-medium mb-1">Projects</div>
                    <div className="text-sm text-blue-700">View available opportunities</div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["quick-start-guide"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("quick-start-guide", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("quick-start-guide", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("quick-start-guide") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("quick-start-guide")}
            >
              {bookmarkedArticles.includes("quick-start-guide") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (activePage === "account-setup") {
    return (
      <div className="space-y-6" id="account-setup">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Account Setup</h1>
          <p className="text-gray-500 mt-2">
            Complete guide to setting up your DGK Platform account for optimal use.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: July 5, 2023</span>
            <Badge variant="outline" className="ml-2">Beginner</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="create-your-account">1. Create Your Account</h2>
          <p>
            The first step to joining the DGK Platform ecosystem is creating your account. 
            We offer multiple authentication methods to suit your preferences.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <div className="flex flex-col p-5 border rounded-lg">
              <div className="flex items-center mb-4">
                <div className="mr-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600">
                    <Mail className="h-5 w-5" />
                  </div>
                </div>
                <h3 className="text-lg font-medium">Email & Password</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Sign up with your email address and create a secure password. 
                We'll send a verification email to confirm your account.
              </p>
              <div className="bg-gray-50 p-3 rounded-md text-sm mt-auto">
                <strong>Tip:</strong> Use a unique password with at least 8 characters, 
                including uppercase letters, numbers, and special characters.
              </div>
            </div>
            
            <div className="flex flex-col p-5 border rounded-lg">
              <div className="flex items-center mb-4">
                <div className="mr-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600">
                    <Globe className="h-5 w-5" />
                  </div>
                </div>
                <h3 className="text-lg font-medium">Social Login</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Connect with your existing accounts from Google, LinkedIn, GitHub, 
                or Microsoft for single-click access to the platform.
              </p>
              <div className="bg-gray-50 p-3 rounded-md text-sm mt-auto">
                <strong>Tip:</strong> Social login provides added security and eliminates 
                the need to remember another password.
              </div>
            </div>
          </div>

          <h2 id="complete-profile">2. Complete Your Profile</h2>
          <p>
            A complete profile is essential for making the most of the DGK Platform. 
            Your profile information helps our AI matching algorithms connect you with 
            relevant opportunities and showcase your expertise.
          </p>
          
          <div className="space-y-5 my-6">
            <div className="flex items-start bg-gray-50 p-5 rounded-lg">
              <UserCircle className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-medium mb-1">Personal Information</h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Full name and professional headline</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Professional profile photo (preferably headshot)</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Contact information (visible only to connected users)</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Location (enables region-specific opportunities)</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="flex items-start bg-gray-50 p-5 rounded-lg">
              <Briefcase className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-medium mb-1">Professional Background</h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Skills with proficiency levels</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Work experience (current and previous roles)</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Education and certifications</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Languages spoken and written</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="flex items-start bg-gray-50 p-5 rounded-lg">
              <Image className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-medium mb-1">Portfolio Items</h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Project samples (at least 3 recommended)</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Case studies of past work</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Testimonials and references</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Awards and recognition</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 my-6">
            <h3 className="text-base font-medium text-blue-800 mb-2 flex items-center">
              <Info className="h-5 w-5 mr-2" />
              Profile Completion Bonus
            </h3>
            <p className="text-blue-700 text-sm">
              Profiles with 100% completion receive priority in search results and AI matching algorithms. 
              Complete your profile to unlock enhanced visibility features and premium dashboard insights.
            </p>
          </div>

          <h2 id="security-settings">3. Configure Security Settings</h2>
          <p>
            Protecting your account is crucial on the DGK Platform. We recommend configuring 
            these security measures as part of your initial account setup:
          </p>
          
          <div className="space-y-4 my-6">
            <div className="flex items-start">
              <ShieldCheck className="h-5 w-5 text-emerald-600 mr-3 mt-1" />
              <div>
                <h3 className="font-medium">Two-Factor Authentication (2FA)</h3>
                <p className="text-gray-600 text-sm mt-1">
                  Add an extra layer of security by enabling 2FA. You can use an authenticator app 
                  or receive verification codes via SMS.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <KeyRound className="h-5 w-5 text-emerald-600 mr-3 mt-1" />
              <div>
                <h3 className="font-medium">Recovery Options</h3>
                <p className="text-gray-600 text-sm mt-1">
                  Set up alternative email addresses and recovery phone numbers to regain access 
                  to your account if needed.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <Smartphone className="h-5 w-5 text-emerald-600 mr-3 mt-1" />
              <div>
                <h3 className="font-medium">Device Management</h3>
                <p className="text-gray-600 text-sm mt-1">
                  Review and manage devices that have access to your account. You can revoke access 
                  from any device remotely.
                </p>
              </div>
            </div>
          </div>

          <h2 id="notification-preferences">4. Set Notification Preferences</h2>
          <p>
            Stay informed about important activity without being overwhelmed. Configure your 
            notification settings based on your preferences:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center mb-3">
                  <Bell className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="font-medium">Important Notifications</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>New project invitations</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Direct messages</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Payment receipts</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center mb-3">
                  <Settings className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="font-medium">Notification Channels</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Email notifications</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>In-app notifications</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-emerald-500 mr-2" />
                    <span>Mobile push notifications</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["account-setup"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("account-setup", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("account-setup", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("account-setup") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("account-setup")}
            >
              {bookmarkedArticles.includes("account-setup") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (activePage === "dashboard-tour") {
    return (
      <div className="space-y-6" id="dashboard-tour">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard Tour</h1>
          <p className="text-gray-500 mt-2">
            An overview of the DGK Platform dashboard and its key features.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: July 10, 2023</span>
            <Badge variant="outline" className="ml-2">Beginner</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="dashboard-overview">Dashboard Overview</h2>
          <p>
            The DGK Platform dashboard is your command center for managing all aspects of your 
            freelance business or client projects. It provides at-a-glance information and 
            quick access to essential features.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 mr-3">
                  <LayoutDashboard className="h-4 w-4" />
                </div>
                <h3 className="font-medium">Main Dashboard</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Your personalized home screen with activity feeds, stats, and recommended actions. 
                This is the first screen you see after logging in.
              </p>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-600 mr-3">
                  <Search className="h-4 w-4" />
                </div>
                <h3 className="font-medium">Global Search</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Quickly find projects, people, messages, and other content from anywhere in 
                the platform using the global search bar.
              </p>
            </div>
          </div>
          
          <h2 id="key-dashboard-sections">Key Dashboard Sections</h2>
          <div className="space-y-6 my-6">
            <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
              <div className="flex items-center mb-3">
                <Gauge className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-medium">Performance Metrics</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Track your key performance indicators including earnings, project completion rate, 
                client satisfaction, and more.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-white p-3 rounded border border-gray-100">
                  <div className="text-sm text-gray-500">Active Projects</div>
                  <div className="text-xl font-semibold mt-1 text-gray-900">5</div>
                </div>
                <div className="bg-white p-3 rounded border border-gray-100">
                  <div className="text-sm text-gray-500">Completion Rate</div>
                  <div className="text-xl font-semibold mt-1 text-gray-900">98%</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
              <div className="flex items-center mb-3">
                <FileText className="h-5 w-5 text-green-600 mr-2" />
                <h3 className="text-lg font-medium">Project Tracking</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Monitor all your ongoing projects, with detailed progress tracking, upcoming 
                deadlines, and client communications.
              </p>
              <div className="bg-white p-3 rounded border border-gray-100">
                <h4 className="font-medium text-sm mb-2">Recent Projects</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between">
                    <span>Website Redesign</span>
                    <span className="text-green-600">In Progress</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Logo Design</span>
                    <span className="text-amber-600">Review</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
              <div className="flex items-center mb-3">
                <BarChart className="h-5 w-5 text-purple-600 mr-2" />
                <h3 className="text-lg font-medium">Analytics</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Gain insights from detailed analytics about your performance, earnings, 
                profile views, and proposal success rates.
              </p>
              <div className="bg-white p-3 rounded border border-gray-100 text-center">
                <div className="text-sm text-gray-500 mb-1">Profile Views (Last 30 Days)</div>
                <div className="h-12 bg-gray-50 rounded-md mx-auto w-full max-w-xs">
                  {/* Chart visualization placeholder */}
                  <div className="flex items-end justify-between h-full p-1">
                    <div className="w-1 rounded-t bg-blue-400 h-3"></div>
                    <div className="w-1 rounded-t bg-blue-400 h-5"></div>
                    <div className="w-1 rounded-t bg-blue-400 h-4"></div>
                    <div className="w-1 rounded-t bg-blue-400 h-7"></div>
                    <div className="w-1 rounded-t bg-blue-400 h-9"></div>
                    <div className="w-1 rounded-t bg-blue-400 h-6"></div>
                    <div className="w-1 rounded-t bg-blue-400 h-8"></div>
                  </div>
                </div>
                <div className="text-xs text-gray-400 mt-1">May 15 - June 15</div>
              </div>
            </div>
          </div>
          
          <h2 id="navigation-sidebars">Navigation & Sidebars</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <div>
              <h3 className="text-base font-medium mb-3">Main Navigation</h3>
              <ul className="space-y-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <li className="flex items-center text-blue-600 font-medium">
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  <span>Dashboard</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <Briefcase className="h-4 w-4 mr-2" />
                  <span>Projects</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  <span>Messages</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <CircleDollarSign className="h-4 w-4 mr-2" />
                  <span>Finances</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <Book className="h-4 w-4 mr-2" />
                  <span>Portfolio</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-base font-medium mb-3">Quick Actions</h3>
              <ul className="space-y-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <li>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule Meeting
                  </Button>
                </li>
                <li>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <FileText className="h-4 w-4 mr-2" />
                    Create Proposal
                  </Button>
                </li>
                <li>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Contact Support
                  </Button>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 my-6">
            <h3 className="text-base font-medium text-blue-800 mb-2 flex items-center">
              <Info className="h-5 w-5 mr-2" />
              Customization Tips
            </h3>
            <p className="text-blue-700 text-sm">
              You can customize your dashboard layout and widgets by clicking on the "Customize" 
              button in the top-right corner of your dashboard. Arrange widgets in the order that 
              makes the most sense for your workflow.
            </p>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["dashboard-tour"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("dashboard-tour", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("dashboard-tour", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("dashboard-tour") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("dashboard-tour")}
            >
              {bookmarkedArticles.includes("dashboard-tour") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (activePage === "glossary") {
    return (
      <div className="space-y-6" id="glossary">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Glossary of Terms</h1>
          <p className="text-gray-500 mt-2">
            Definitions of key terms and concepts used throughout the DGK Platform.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: July 15, 2023</span>
            <Badge variant="outline" className="ml-2">Reference</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <p>
            This glossary provides definitions for common terms used throughout the DGK Platform 
            and documentation. Refer to this guide whenever you encounter unfamiliar terminology.
          </p>
          
          <div className="my-6">
            <div className="flex items-center space-x-2 mb-4">
              <BookOpen className="h-5 w-5 text-blue-600" />
              <h2 className="text-xl font-bold tracking-tight m-0">Platform Terminology</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 id="ai-job-matching" className="text-lg font-medium border-b pb-1">AI Job Matching</h3>
                <p className="text-gray-700">
                  An algorithm-based feature that analyzes your skills, experience, and preferences 
                  to recommend suitable job opportunities, increasing the likelihood of successful matches 
                  between freelancers and clients.
                </p>
              </div>
              
              <div>
                <h3 id="ai-agents" className="text-lg font-medium border-b pb-1">AI Agents</h3>
                <p className="text-gray-700">
                  Intelligent software assistants that help automate tasks, provide insights, and 
                  enhance productivity across various platform functions including content creation, 
                  scheduling, and project management.
                </p>
              </div>
              
              <div>
                <h3 id="digital-portfolio" className="text-lg font-medium border-b pb-1">Digital Portfolio</h3>
                <p className="text-gray-700">
                  A customizable online showcase of your work, skills, and achievements that helps 
                  potential clients evaluate your capabilities and expertise. Can include project samples, 
                  testimonials, case studies, and other relevant work.
                </p>
              </div>
              
              <div>
                <h3 id="escrow" className="text-lg font-medium border-b pb-1">Escrow</h3>
                <p className="text-gray-700">
                  A secure payment holding system where client funds are safely stored until project 
                  milestones or deliverables are completed and approved, protecting both parties 
                  in a transaction.
                </p>
              </div>
              
              <div>
                <h3 id="marketplace" className="text-lg font-medium border-b pb-1">Marketplace</h3>
                <p className="text-gray-700">
                  The central hub where freelancers offer services and clients post projects, 
                  facilitating the discovery, negotiation, and execution of professional services 
                  transactions.
                </p>
              </div>
              
              <div>
                <h3 id="proposal" className="text-lg font-medium border-b pb-1">Proposal</h3>
                <p className="text-gray-700">
                  A formal application submitted by a freelancer in response to a client's project 
                  posting, outlining approach, timeline, costs, and qualifications for completing 
                  the requested work.
                </p>
              </div>
              
              <div>
                <h3 id="skills-test" className="text-lg font-medium border-b pb-1">Skills Test</h3>
                <p className="text-gray-700">
                  Standardized assessments that verify proficiency in specific skills, helping 
                  freelancers demonstrate their expertise and clients identify qualified professionals 
                  for their projects.
                </p>
              </div>
              
              <div>
                <h3 id="team-builder" className="text-lg font-medium border-b pb-1">Team Builder</h3>
                <p className="text-gray-700">
                  A feature that helps assemble optimal teams for projects by matching complementary 
                  skills, availability, and experience levels among freelancers in the platform's 
                  talent pool.
                </p>
              </div>
            </div>
          </div>
          
          <div className="my-6">
            <div className="flex items-center space-x-2 mb-4">
              <User className="h-5 w-5 text-purple-600" />
              <h2 className="text-xl font-bold tracking-tight m-0">User Roles & Accounts</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 id="freelancer" className="text-lg font-medium border-b pb-1">Freelancer</h3>
                <p className="text-gray-700">
                  An independent professional who offers services on the platform, works on a 
                  project basis, and typically specializes in specific skills or expertise areas.
                </p>
              </div>
              
              <div>
                <h3 id="client" className="text-lg font-medium border-b pb-1">Client</h3>
                <p className="text-gray-700">
                  An individual or organization that posts projects, hires freelancers, and pays 
                  for services through the platform.
                </p>
              </div>
              
              <div>
                <h3 id="agency" className="text-lg font-medium border-b pb-1">Agency</h3>
                <p className="text-gray-700">
                  A company or organized group of professionals that offers combined services, 
                  manages multiple freelancers, and takes on larger or more complex projects.
                </p>
              </div>
              
              <div>
                <h3 id="enterprise" className="text-lg font-medium border-b pb-1">Enterprise</h3>
                <p className="text-gray-700">
                  Large companies with specialized platform access, custom features, dedicated 
                  support, and tools designed for managing freelance workforces at scale.
                </p>
              </div>
              
              <div>
                <h3 id="verified" className="text-lg font-medium border-b pb-1">Verified User</h3>
                <p className="text-gray-700">
                  A user who has completed identity verification processes, providing additional 
                  trustworthiness on the platform through validated personal or business information.
                </p>
              </div>
            </div>
          </div>
          
          <div className="my-6">
            <div className="flex items-center space-x-2 mb-4">
              <Briefcase className="h-5 w-5 text-emerald-600" />
              <h2 className="text-xl font-bold tracking-tight m-0">Project & Payment Terms</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 id="fixed-price" className="text-lg font-medium border-b pb-1">Fixed-Price Project</h3>
                <p className="text-gray-700">
                  A project with a predetermined total cost agreed upon before work begins, 
                  regardless of the time or resources required to complete it.
                </p>
              </div>
              
              <div>
                <h3 id="hourly" className="text-lg font-medium border-b pb-1">Hourly Project</h3>
                <p className="text-gray-700">
                  A project billed based on the actual time spent working, tracked through the 
                  platform's time-tracking tools and paid at an agreed-upon daily rate.
                </p>
              </div>
              
              <div>
                <h3 id="milestone" className="text-lg font-medium border-b pb-1">Milestone</h3>
                <p className="text-gray-700">
                  A distinct, measurable checkpoint in a project that marks significant progress, 
                  often tied to partial payments and used to break larger projects into manageable 
                  segments.
                </p>
              </div>
              
              <div>
                <h3 id="retainer" className="text-lg font-medium border-b pb-1">Retainer</h3>
                <p className="text-gray-700">
                  An ongoing arrangement where a client pays in advance for reserved access to a 
                  freelancer's services for a specified period, often with predefined deliverables 
                  or hours.
                </p>
              </div>
              
              <div>
                <h3 id="service-fee" className="text-lg font-medium border-b pb-1">Service Fee</h3>
                <p className="text-gray-700">
                  A percentage-based fee charged by the platform for facilitating transactions, 
                  providing secure payment processing, and offering platform services and protection.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 my-6">
            <h3 className="text-base font-medium text-blue-800 mb-2 flex items-center">
              <Info className="h-5 w-5 mr-2" />
              Terminology Tip
            </h3>
            <p className="text-blue-700 text-sm">
              Understanding platform-specific terminology helps you navigate features more efficiently 
              and communicate effectively with other users. Bookmark this glossary for quick reference 
              when needed.
            </p>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["glossary"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("glossary", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("glossary", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("glossary") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("glossary")}
            >
              {bookmarkedArticles.includes("glossary") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-[400px] bg-gray-50 rounded-lg border border-dashed border-gray-300">
      <div className="text-center max-w-md p-6">
        <BookOpen className="h-10 w-10 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Documentation Coming Soon</h3>
        <p className="text-gray-500">
          We're currently working on this documentation section. Check back soon for updates!
        </p>
      </div>
    </div>
  );
};
